import type { Point } from './types'

interface Node {
  x: number
  y: number
  g: number
  h: number
  f: number
  parent: Node | null
}

function heuristic(a: Point, b: Point): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y)
}

function getNeighbors(node: Node, grid: boolean[][], width: number, height: number): Point[] {
  const neighbors: Point[] = []
  const directions = [
    { x: 0, y: -1 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: -1, y: 0 }
  ]

  for (const dir of directions) {
    const nx = node.x + dir.x
    const ny = node.y + dir.y
    if (nx >= 0 && nx < width && ny >= 0 && ny < height && grid[ny][nx]) {
      neighbors.push({ x: nx, y: ny })
    }
  }

  return neighbors
}

function reconstructPath(node: Node): Point[] {
  const path: Point[] = []
  let current: Node | null = node

  while (current !== null) {
    path.unshift({ x: current.x, y: current.y })
    current = current.parent
  }

  return path
}

export function findPath(
  grid: boolean[][],
  start: Point,
  end: Point
): Point[] | null {
  if (grid.length === 0 || grid[0].length === 0) return null

  const height = grid.length
  const width = grid[0].length

  if (!grid[start.y]?.[start.x] || !grid[end.y]?.[end.x]) {
    return null
  }

  const openSet: Node[] = []
  const closedSet = new Set<string>()

  const startNode: Node = {
    x: start.x,
    y: start.y,
    g: 0,
    h: heuristic(start, end),
    f: heuristic(start, end),
    parent: null
  }

  openSet.push(startNode)

  while (openSet.length > 0) {
    openSet.sort((a, b) => a.f - b.f)
    const current = openSet.shift()!

    if (current.x === end.x && current.y === end.y) {
      return reconstructPath(current)
    }

    const key = `${current.x},${current.y}`
    if (closedSet.has(key)) continue
    closedSet.add(key)

    const neighbors = getNeighbors(current, grid, width, height)

    for (const neighbor of neighbors) {
      const neighborKey = `${neighbor.x},${neighbor.y}`
      if (closedSet.has(neighborKey)) continue

      const g = current.g + 1
      const h = heuristic(neighbor, end)
      const f = g + h

      const existingNode = openSet.find(n => n.x === neighbor.x && n.y === neighbor.y)

      if (existingNode) {
        if (g < existingNode.g) {
          existingNode.g = g
          existingNode.f = f
          existingNode.parent = current
        }
      } else {
        openSet.push({
          x: neighbor.x,
          y: neighbor.y,
          g,
          h,
          f,
          parent: current
        })
      }
    }
  }

  return null
}

export function canPlaceTower(
  grid: boolean[][],
  start: Point,
  end: Point,
  towerPosition: Point
): boolean {
  if (!grid[towerPosition.y]?.[towerPosition.x]) {
    return false
  }

  if (
    (towerPosition.x === start.x && towerPosition.y === start.y) ||
    (towerPosition.x === end.x && towerPosition.y === end.y)
  ) {
    return false
  }

  const testGrid = grid.map(row => [...row])
  testGrid[towerPosition.y][towerPosition.x] = false

  return findPath(testGrid, start, end) !== null
}

export function createEmptyGrid(width: number, height: number): boolean[][] {
  const grid: boolean[][] = []
  for (let y = 0; y < height; y++) {
    grid[y] = []
    for (let x = 0; x < width; x++) {
      grid[y][x] = true
    }
  }
  return grid
}

export function distanceBetween(a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return Math.sqrt(dx * dx + dy * dy)
}
